<template>
  <span class="AiButton-content">
    <button
      :class="[
        `AiButton AiButton--${variant}`,
        { 'ai-button--disabled': disabled },
      ]"
      role="button"
      :aria-busy="disabled"
      :disabled="disabled"
      :aria-expanded="disabled"
      :aria-label="$t('accessibility.description.controlMedia')"
      @click="$emit('AiButton::click')">
      <slot>
        <ai-icon
          v-if="pause"
          no-padding
          name="pause"
          :size="12"
          class="AiPause--icon" />
        <ai-icon
          v-else
          no-padding
          name="play"
          :size="12"
          class="AiPlay--icon" />
      </slot>
    </button>
    <ai-typo
      v-if="buttonLabel"
      :class="['AiButton-label', `AiButton-label--${variant}`]"
      as="p"
      variant="button-bold">
      {{ buttonLabel }}
    </ai-typo>
  </span>
</template>

<script setup lang="ts">
import AiIcon from '../AiIcon/AiIcon.vue';
import AiTypo from '../AiTypo/AiTypo.vue';

export interface AiButtonProps {
  buttonLabel: string;
  disabled?: boolean;
  pause?: boolean;
  variant: 'positive' | 'inverse';
}

defineEmits(['AiButton::click']);

withDefaults(defineProps<AiButtonProps>(), {
  pause: false,
  variant: 'positive',
});
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/fonts';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';
@use '@/assets/styles/utilities/constants';

.AiButton-content {
  display: flex;
  align-items: center;
}
.AiButton {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.2s ease;

  @include mixins.rem-fallback(padding, 9, 19);
  @include mixins.rem-fallback(height, 64);
  @include mixins.rem-fallback(width, 64);
  @include mixins.rem-fallback(margin, 0);
  @include mixins.rem-fallback(gap, 4);
  @include mixins.rem-fallback(border-radius, 60);

  &--disabled {
    color: colors.$neutral-600 !important;
    background-color: colors.$neutral-200 !important;
    cursor: not-allowed;
  }

  &--positive {
    background: colors.$white;
    color: colors.$positiveBorder;
    border: solid 1px colors.$positiveBorder;

    @media (any-hover: hover) and (prefers-reduced-motion: no-preference) {
      &:hover {
        background: colors.$stratos-700;
        color: colors.$white;
        border: none;
      }
    }
    &:active {
      background: colors.$active;
      color: colors.$white;
      border: none;
    }
    &:focus {
      background: colors.$white;
    }
  }

  &--inverse {
    color: colors.$white;
    border: solid 1px colors.$white;
    position: relative;
    @include mixins.rem-fallback(padding, 9, 18);

    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background: rgba(5, 0, 51, 0.45);
      transform: scale(0.8);
      @include mixins.rem-fallback(border-radius, 60);
    }
    @media (any-hover: hover) and (prefers-reduced-motion: no-preference) {
      &:hover {
        &::after {
          display: none;
        }
        background: colors.$white;
        color: colors.$stratos-900;
      }
    }
    &:active {
      background: colors.$white;
      color: colors.$stratos-900;
      &::after {
        display: none;
      }
    }
    &:focus {
      background: colors.$white;
      color: colors.$stratos-900;
      &::after {
        display: none;
      }
    }
  }
  &-label {
    margin-left: constants.$margin-01;

    @media (mq.$small) {
      display: none;
    }
    &--inverse {
      color: colors.$white;
    }
  }
}
.AiPlay--icon {
  z-index: 1;
  margin-bottom: constants.$inner-01;
  margin-left: constants.$inner-00;
}
</style>
